/*
 * The round's clock running out, as a sound.
 *
 * Synthesised rather than shipped as a file. Three short bell tones are a few
 * lines of WebAudio and no bytes at all, and a facilitator running a workshop
 * off hotel wifi does not want the one moment the room is listening for to be
 * waiting on a network fetch that a cold cache never made.
 *
 * Everything here is best-effort by design. Browsers refuse audio until the
 * page has been interacted with, some contexts start suspended and never
 * resume, and a device may have no audio output at all — none of which is worth
 * an error toast in front of a client, because the clock also turns amber and
 * the bar shakes. So every failure path is a silent return: the sound is the
 * bonus cue, never the only one.
 *
 * That first restriction is why `primeChime` exists: the context has to be
 * opened inside a gesture, and the moment it is needed is not one.
 */

const DINGS = 3
/** Far enough apart to read as three, close enough to read as one signal. */
const DING_GAP_S = 0.24
const DING_LENGTH_S = 0.5
/** B5. High enough to carry over a room talking, short enough not to nag. */
const FUNDAMENTAL_HZ = 987.77
/** A bell is its fundamental plus a thin, slightly detuned octave above it. */
const PARTIALS: readonly (readonly [ratio: number, level: number])[] = [
  [1, 1],
  [2.02, 0.32],
]
const PEAK_GAIN = 0.16

/*
 * One context for the tab. Browsers cap how many a page may open, and a
 * workshop rings this bell once a round for an hour.
 */
let shared: AudioContext | null = null

function context(): AudioContext | null {
  if (shared) return shared
  const Ctor =
    window.AudioContext ??
    (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  try {
    shared = new Ctor()
  } catch {
    return null
  }
  return shared
}

/** One bell, scheduled at `at` on the context's own clock. */
function ding(ctx: AudioContext, at: number) {
  const envelope = ctx.createGain()
  /*
   * Exponential ramps cannot touch zero, so the envelope runs between a
   * near-silent floor and the peak. A linear fade would click; the whole point
   * of the shape is that it decays like something struck.
   */
  envelope.gain.setValueAtTime(0.0001, at)
  envelope.gain.exponentialRampToValueAtTime(PEAK_GAIN, at + 0.012)
  envelope.gain.exponentialRampToValueAtTime(0.0001, at + DING_LENGTH_S)
  envelope.connect(ctx.destination)

  PARTIALS.forEach(([ratio, level], index) => {
    const partial = ctx.createGain()
    partial.gain.value = level
    partial.connect(envelope)

    const osc = ctx.createOscillator()
    osc.type = "sine"
    osc.frequency.setValueAtTime(FUNDAMENTAL_HZ * ratio, at)
    osc.connect(partial)
    osc.start(at)
    osc.stop(at + DING_LENGTH_S)
    osc.onended = () => {
      osc.disconnect()
      partial.disconnect()
      // The last voice to finish takes the envelope with it, so an hour of
      // rounds does not leave a chain of dead nodes on the destination.
      if (index === PARTIALS.length - 1) envelope.disconnect()
    }
  })
}

/**
 * Opens the shared context from a user gesture, so the bell can sound later.
 *
 * Safari and every other autoplay-restricted browser start a context suspended
 * unless it was created inside a user activation, and a `resume()` from the
 * timer callback — which is not one — cannot unlock it afterwards. So the
 * facilitator's press on the clock is what opens it, minutes before there is
 * anything to play. Silent and harmless everywhere else; `playTimeUpChime`
 * reuses whatever this left behind.
 */
export function primeChime(): void {
  const ctx = context()
  if (!ctx) return
  try {
    void ctx.resume().catch(() => {})
  } catch {
    // Nothing to recover: see the note at the top of the file.
  }
}

/** Ding ding ding. Silent, and harmless, wherever audio is not available. */
export function playTimeUpChime(): void {
  const ctx = context()
  if (!ctx) return
  try {
    /*
     * A context created before the first gesture starts suspended, and its
     * clock does not advance while it is: notes scheduled now are held and
     * played the moment it resumes, so this is a request, not a precondition.
     */
    void ctx.resume().catch(() => {})
    const start = ctx.currentTime + 0.02
    for (let i = 0; i < DINGS; i += 1) ding(ctx, start + i * DING_GAP_S)
  } catch {
    // Nothing to recover: see the note at the top of the file.
  }
}
